import { useEffect, useRef } from 'react';
import axios from 'axios';

const POLL_INTERVAL_MS = (() => {
  const val = Number(process.env.REACT_APP_SESSION_LIVENESS_POLL_MS);
  return !isNaN(val) && val > 0 ? val : 3 * 60 * 1000;
})(); // default 3 minutes

// Catches a session killed directly in the Keycloak Admin Console — the backend's normal
// auth check only looks at our own local Sessions table (see jwt_auth.js), which a
// Keycloak-side kill never touches, so nothing else in the app notices this on its own.
export function useSessionLivenessPoll(enabled = true) {
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!enabled) return;

    const check = () => {
      // Read fresh each tick (not once at effect-setup) so a token rotated by a silent
      // refresh in between polls is picked up, and a cleared token after logout makes
      // this a harmless no-op rather than sending a stale one.
      let stored;
      try {
        stored = JSON.parse(sessionStorage.getItem('tokens') || localStorage.getItem('tokens') || 'null');
      } catch {
        stored = null;
      }

      // Feature-detect: only Keycloak-flag-on sessions carry session_id — mirrors the
      // same check axiosInterceptor.js uses to gate its refresh-retry logic.
      if (!stored?.session_id || !stored?.token) return;

      axios.get(`${process.env.REACT_APP_API_BASE_URL}/officer/session/validate`, {
        headers: { Authorization: `Bearer ${stored.token}` },
      }).catch(() => {
        // A 401 here (SESSION_INVALID) is handled globally by axiosInterceptor.js's
        // response interceptor (forceLogout) — nothing to do in this catch.
      });
    };

    check(); // once immediately on mount/refresh
    intervalRef.current = setInterval(check, POLL_INTERVAL_MS);

    return () => clearInterval(intervalRef.current);
  }, [enabled]);
}
